import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Checkbox } from '@delmonicos/shared-design-system-react/src/components/checkbox/checkbox';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useCreateTariffFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/create-tariff.form-validation';
import { CreateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/create-tariff.model';
import { TariffDimensionType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTariffUIContext } from '@app/feature/tariff/tariff.context';

import * as css from '../tariff-create-form-step-3/tariff-create-form-step-3.module.scss';

interface PriceSelectionProps {
  elementIndex: number;
  priceComponentIndex: number;
}

const EnergySelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<CreateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useCreateTariffFormValidation();

  useEffect(() => {
    setValue(
      `elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.ENERGY,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                required
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-create.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                required
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>kWh</Text>
      </Stack>
    </Stack>
  );
};

const TimeSelection = ({
  elementIndex,
  priceComponentIndex,
}: PriceSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<CreateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useCreateTariffFormValidation();

  useEffect(() => {
    setValue(
      `elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
          control={control}
          rules={priceComponentPrice(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-price-component.price')}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />
        <Text color={TextColor.Secondary}>{getValues('currency')}</Text>
      </Stack>

      <Text color={TextColor.Secondary}>{t('$.tariff-create.step-3.by')}</Text>

      <Stack gap={GapEnum.S} alignItems="center">
        <Controller
          name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
          control={control}
          rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                label={t(
                  'delmonicos-shared-sdk.outputs-price-component.step-size',
                )}
                placeholder="0"
                errorMessage={error?.message}
                type="number"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? '' : e.target.valueAsNumber,
                  )
                }
              />
            );
          }}
        />

        <Text color={TextColor.Secondary}>min</Text>
      </Stack>
    </Stack>
  );
};

interface ParkingSelectionProps extends PriceSelectionProps {
  hasParkingPrice: boolean;
  setHasParkingPrice: (value: boolean) => void;
}

const ParkingSelection = ({
  elementIndex,
  priceComponentIndex,
  hasParkingPrice,
  setHasParkingPrice,
}: ParkingSelectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues, control, setValue } = useFormContext<CreateTariffModel>();
  const { priceComponentPrice, priceComponentStepSize } =
    useCreateTariffFormValidation();

  useEffect(() => {
    setValue(
      `elements.${elementIndex}.priceComponents.${priceComponentIndex}.type`,
      TariffDimensionType.PARKING_TIME,
    );
  }, [elementIndex, priceComponentIndex, setValue]);

  return (
    <Stack gap={GapEnum.M} direction="column">
      <Checkbox
        id="different-tariff-after-charge"
        label={t('$.tariff-create.step-3.parking.different-tariff.label')}
        checked={hasParkingPrice}
        onChange={(e) => setHasParkingPrice(e.target.checked)}
      />
      {hasParkingPrice && (
        <Stack gap={GapEnum.M} alignItems="center" flexWrap="wrap">
          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.price`}
              control={control}
              rules={priceComponentPrice(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.price',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>{getValues('currency')}</Text>
          </Stack>

          <Text color={TextColor.Secondary}>
            {t('$.tariff-create.step-3.by')}
          </Text>

          <Stack gap={GapEnum.S} alignItems="center">
            <Controller
              name={`elements.${elementIndex}.priceComponents.${priceComponentIndex}.stepSize`}
              control={control}
              rules={priceComponentStepSize(elementIndex, priceComponentIndex)}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextInput
                    label={t(
                      'delmonicos-shared-sdk.outputs-price-component.step-size',
                    )}
                    placeholder="0"
                    errorMessage={error?.message}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : e.target.valueAsNumber,
                      )
                    }
                  />
                );
              }}
            />
            <Text color={TextColor.Secondary}>min</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const TariffTimeSlot = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { firstRange, setFirstRange } = useTariffUIContext();

  const setHasParkingPrice = (value: boolean) => {
    const newFirstRange = { ...firstRange };
    newFirstRange.firstTimeSlot.hasParkingPrice = value;
    setFirstRange(newFirstRange);
  };

  return (
    <Stack gap={GapEnum.S} flex={1} direction="column">
      <Card flex={1} gap={GapEnum.M}>
        <AccentTitle
          icon="lightning-01"
          text={t('$.tariff-create.step-2-alternate.energy')}
        />
        <EnergySelection elementIndex={0} priceComponentIndex={0} />
      </Card>

      <div style={{ alignSelf: 'center' }}>
        <Text>+</Text>
      </div>

      <Card flex={1} gap={GapEnum.M} direction="column">
        <AccentTitle
          icon="clock-fast-forward"
          text={t('$.tariff-create.step-2-alternate.time')}
          variant={AccentVariantEnum.Secondary}
        />
        <TimeSelection elementIndex={0} priceComponentIndex={1} />
        <Divider />
        <ParkingSelection
          elementIndex={0}
          priceComponentIndex={2}
          hasParkingPrice={firstRange.firstTimeSlot.hasParkingPrice}
          setHasParkingPrice={setHasParkingPrice}
        />
      </Card>
    </Stack>
  );
};

interface PreviewCardTimeSlotProps {
  currency: string;
}

const PreviewCardTimeSlot = ({
  currency,
}: PreviewCardTimeSlotProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { flatValue, firstRange } = useTariffUIContext();
  const { watch } = useFormContext<CreateTariffModel>();
  const energy = watch('elements.0.priceComponents.0');
  const time = watch('elements.0.priceComponents.1');
  const parking = watch('elements.0.priceComponents.2');

  return (
    <Card flex={1} gap={GapEnum.M} className={css.timeSlotPreview}>
      <Stack gap={GapEnum.S} alignItems="center">
        <Icon name="price" />
        <Text bold>
          {t('$.tariff-create.step-2-alternate.preview.time-slot.tariff')}
        </Text>
      </Stack>

      <Stack gap={GapEnum.S} flex={1}>
        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Text bold color={TextColor.Secondary}>
            {energy.price ?? '-'} {currency} / {energy.stepSize ?? '-'} kWh
          </Text>
        </Card>

        <Stack alignItems="center">
          <Text bold>+</Text>
        </Stack>

        <Card
          flex={1}
          gap={GapEnum.M}
          alignItems="center"
          justifyContent="center">
          <Stack flex={1} alignItems="center" justifyContent="center">
            <Text bold color={TextColor.Secondary}>
              {time?.price ?? '-'} {currency} / {time?.stepSize ?? '-'} min
            </Text>
          </Stack>

          {firstRange.firstTimeSlot.hasParkingPrice && (
            <Stack
              flex={1}
              alignItems="center"
              justifyContent="center"
              className={css.parkingPreview}>
              <Text color={TextColor.Secondary}>
                {parking?.price ?? '-'} {currency} / {parking?.stepSize ?? '-'}{' '}
                min
              </Text>
            </Stack>
          )}
        </Card>
      </Stack>

      {flatValue && (
        <Stack justifyContent="space-between" alignItems="center">
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon name="home-03" />
            <Text>{t('$.tariff-create.step-3.preview.flat-value')}</Text>
          </Stack>
          <Text bold>
            {flatValue} {currency}
          </Text>
        </Stack>
      )}
    </Card>
  );
};

const PreviewCardRangeFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<CreateTariffModel>();

  return (
    <Card gap={GapEnum.M} className={css.previewCard}>
      <AccentTitle
        icon="eye"
        text={t('$.tariff-create.step-3.preview.title')}
        variant={AccentVariantEnum.Unavailable}
      />

      <PreviewCardTimeSlot currency={getValues('currency')} />
    </Card>
  );
};

export const TariffCreateFormStep2AlternateComponent =
  (): React.JSX.Element => {
    return (
      <Stack gap={GapEnum.M} direction="column" flex={1}>
        <Stack gap={GapEnum.M} flex={1}>
          <TariffTimeSlot />

          <PreviewCardRangeFragment />
        </Stack>
      </Stack>
    );
  };
